// 🔧 vote.js
// ✅ Purpose: Allow users to confirm and submit their vote for a car
// ✅ Features:
// - Load car by `regcnt` within current event
// - Show car details and confirm vote
// - Check for existing vote in that category, delete if re-casting
// - Optional: mark vote as "Best in Show"
// - Uses Firestore voting settings for time window and toggle
// - Redirects user appropriately if voting is closed or not allowed
// - Fully commented for clarity

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  serverTimestamp,
  setDoc,
  arrayUnion,
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { useRole } from "../../role_context";
import FullPageSpinner from "../../components/FullPageSpinner";
import { useEventContext } from "../../hooks/use_event_context";
import { useVotingStatus } from "../../hooks/use_voting_status";
import { useEntry } from "../../hooks/use_entry";
import {
  getEventVotingType,
  isStandardVotingEvent,
} from "../../utils/event_voting_type_helpers";
import { logger } from '../../utils/logger';
import { showStatus } from "../../utils/status_helpers";
import {
  buildVoteDocumentForEvent,
  getVoteUiMode,
} from "../../utils/vote_behavior_helpers";
import PointsScorecardForm from "../../components/points_scorecard_form";
import {
  getScoringFactors,
  buildBlankPointsScorecard,
  buildPointsScorecardFromExistingVote,
  preparePointsScoresForSave,
  validateScoringSetupReady,
} from "../../utils/scoring_factor_helpers";
import {
  calculatePointsBallotTotal,
  validatePointsBallot,
} from "../../utils/points_helpers";
import {
  getVotingTypeLabel,
  allowsBestInShow,
} from "../../utils/voting_engine_registry";
import {
  SESSION_KEYS,
  getSessionValue,
  setSessionValue,
  removeSessionValue,
  clearPendingVoteNavigation,
} from "../../utils/browser_storage_helpers";
import SeoHead from "../../components/seo_head";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { trackEvent } from "../../utils/analytics";
import { GA_EVENTS } from "../../services/analytics/ga_events";

const Vote = () => {

  const [user, authLoading, loading] = useAuthState(auth);
  const {
    eventslug,
    eventId,
    eventDoc,
    eventLoading,
    ready,
  } = useEventContext({ redirectIfNotFound: true });

  const { role, roleLoading } = useRole();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isVotingOpen, loading: votingLoading } = useVotingStatus(eventId); // always called
  const [categoriesMap, setCategoriesMap] = useState({});
  const [prevVote, setPrevVote] = useState(null);
  const [loadedPointsVote, setLoadedPointsVote] = useState(null);
  const [status, setStatus] = useState(null);
  const [voteBestInShow, setVoteBestInShow] = useState(false);
  const [selectedRank, setSelectedRank] = useState(1);

  // ✅ Points / Scorecard Voting State
  const [scoringFactors, setScoringFactors] = useState([]);
  const [scorecard, setScorecard] = useState([]);
  const [maxBallotPoints, setMaxBallotPoints] = useState(100);
  const [loadingScorecard, setLoadingScorecard] = useState(false);

  //
  const [bestInShowEnabled, setBestInShowEnabled] = useState(false);
  const { car, loading: carLoading } = useEntry(eventId, id);
  const canonicalEntryId = car?.id || car?.cid || id;
  const categoryName = categoriesMap[String(car?.catid)] || "Category";

  // ✅ Ranked choice currently supports ranking up to 5 entries
  // ✅ Later this can become an event setting
  const maxRankChoices = 5;

  // ✅ Determine how this event handles voting
  const votingType = getEventVotingType(eventDoc);
  const voteUiMode = getVoteUiMode(eventDoc);
  const isStandardVoting = isStandardVotingEvent(eventDoc);
  const votingTypeLabel = getVotingTypeLabel(votingType);

  // ✅ A factor is considered Scorecard Mode if one or more highlights
  // have a maxPoints value greater than zero.
  const isScorecardMode = scoringFactors.some((factor) =>
    (factor.highlights || []).some(
      (highlight) => Number(highlight.maxPoints || 0) > 0
    )
  );

  const showBestInShowOption =
    bestInShowEnabled && allowsBestInShow(votingType);

  useEffect(() => {
    if (!status) return;

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {

    logger.log("🗳 Vote UI mode", {
      voteUiMode,
    });
      
    if (!car) logger.warn("⚠️ Car is not yet loaded", {});

    logger.log("🗳 Event voting type detected", {
      votingType,
    });
  }, [car, votingType]);

  // 🔐 Redirect if user not verified
  useEffect(() => {
    if (!authLoading && user && !user.emailVerified) {
      logger.warn("⛔ User not verified — redirecting to dashboard", {});
      navigate("/dashboard?verifyRequired=1");
    }
  }, [user, authLoading, navigate]);

  // 💾 Save vote intent if user is not logged in
  // ✅ Waits until event + car are loaded so Login can show real entry/category details
  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    if (!id || !eventslug || !eventId || !car) return;

    trackEvent({
      action: GA_EVENTS.VOTE_PAGE_VIEWED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        entry_id: canonicalEntryId,
        category_id: car?.catid || "",
        voting_type: votingType,
        source_page: window.location.pathname,
      },
    });   

    let cancelled = false;

    const saveVoteIntentAndRedirect = async () => {
      try {
        logger.log("🔒 Saving vote intent and redirecting to login", {
          id: canonicalEntryId,
          eventslug,
          eventId,
          car,
        });

        let resolvedCategoryName = categoryName;

        if (car?.catid && eventId) {
          const categoryRef = doc(
            db,
            "events",
            eventId,
            "categories",
            String(car.catid)
          );

          const categorySnap = await getDoc(categoryRef);

          if (categorySnap.exists()) {
            resolvedCategoryName =
              categorySnap.data().name || categoryName || "Category";
          }
        }

        if (cancelled) return;

        setSessionValue(
          SESSION_KEYS.PENDING_VOTE_CID,
          canonicalEntryId
        );

        setSessionValue(
          SESSION_KEYS.LAST_EVENT_SLUG,
          eventslug
        );

        setSessionValue(
          SESSION_KEYS.LAST_EVENT_ID,
          eventId
        );

        setSessionValue(
          SESSION_KEYS.PENDING_VOTE_ENTRY_LABEL,
          car?.description ||
            `${car?.year || ""} ${car?.model || ""}`.trim() ||
            "this entry"
        );

        setSessionValue(
          SESSION_KEYS.PENDING_VOTE_CATEGORY_LABEL,
          resolvedCategoryName || "Category"
        );

        navigate("/login?vote");
      } catch (err) {
        logger.error("❌ Failed saving vote intent:", {
          value: err,
        });

        navigate("/login?vote");
      }
    };

    saveVoteIntentAndRedirect();

    return () => {
      cancelled = true;
    };
  }, [
    authLoading,
    user,
    canonicalEntryId,
    eventslug,
    eventId,
    car,
    categoryName,
    navigate,
  ]);

  // ⚙️ Load settings and categories after user is valid and voting is allowed
  useEffect(() => {

    const fullyReady =
      ready &&
      !authLoading &&
      !roleLoading &&
      !eventLoading &&
      !votingLoading;

      if (!fullyReady) return;

    if (!user || !["voter", "admin", "manager"].includes(role)) {
      navigate(`/${eventslug}/ballot?unauthorized`);
      return;
    }

    if (!isVotingOpen) {
      navigate(`/${eventslug}/ballot?closed`);
      return;
    }

    if (!eventId) return;

    // Load voting settings
    const loadVotingSettings = async () => {
      try {
        const ref = doc(db, "events", eventId, "settings", "voting");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setBestInShowEnabled(data.bestInShowEnabled === true);
          logger.log("🛠 Voting settings loaded:", {
            value: data
          });
        }
      } catch (err) {
        logger.error("❌ Failed to load voting settings:", {
          value: err
        });
      }
    };

    // Load categories
    const loadCategories = async () => {
      try {
        const snap = await getDocs(collection(db, "events", eventId, "categories"));
        const docs = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const map = {};
        for (const doc of docs) {
          map[String(doc.id)] = doc.name;
        }
        setCategoriesMap(map);
        logger.log("✅ categoriesMap loaded:", {
          value: map
        });
      } catch (err) {
        logger.error("❌ Failed to load categories:", {
          value: err
        });
      }
    };

    loadVotingSettings();
    loadCategories();
  }, [authLoading, roleLoading, eventLoading, votingLoading, user, role, isVotingOpen, eventId, eventslug, navigate]);

  // Load Event Scoring Configuration
  useEffect(() => {

    if (!eventId) return;

    if (votingType !== "points") return;

    const loadScoringConfiguration = async () => {

      try {

        setLoadingScorecard(true);

        const loadedFactors =
          await getScoringFactors(eventId);

        // ✅ Resolve the organizer-configured judged ballot maximum
        // during the same operation that initializes the scorecard.
        // ✅ This avoids an async race with the separate general
        // voting-settings loader used elsewhere on this page.
        const votingSettingsSnap = await getDoc(
          doc(db, "events", eventId, "settings", "voting")
        );

        const votingSettingsData =
          votingSettingsSnap.exists()
            ? votingSettingsSnap.data()
            : {};

        // ✅ New organizer-controlled setting is authoritative.
        // ✅ Legacy root event value remains backward compatible.
        // ✅ Existing events with neither value remain 100-point events.
        const resolvedMaxBallotPoints =
          Number(
            votingSettingsData.maxBallotPoints ??
            eventDoc?.maxBallotPoints ??
            100
          );

        console.log(
          "Resolved judged vote ballot maximum:",
          {
            settingsMaxBallotPoints:
              votingSettingsData.maxBallotPoints ?? null,
            legacyEventMaxBallotPoints:
              eventDoc?.maxBallotPoints ?? null,
            resolvedMaxBallotPoints,
          }
        );

        const validation =
          validateScoringSetupReady(
            loadedFactors,
            resolvedMaxBallotPoints
          );

        if (!validation.isReady) {

          showStatus(setStatus, {
            type: "warning",
            message: validation.message,
          });

          return;
        }

        setScoringFactors(loadedFactors);

        setMaxBallotPoints(
          resolvedMaxBallotPoints
        );

        // ✅ If this entry already has a saved Points scorecard,
        // rebuild the form using current scoring factors + saved vote values.
        // ✅ Otherwise, build a fresh blank scorecard.
        if (
          loadedPointsVote &&
          Array.isArray(loadedPointsVote.scores)
        ) {
          setScorecard(
            buildPointsScorecardFromExistingVote(
              loadedFactors,
              loadedPointsVote.scores
            )
          );
        } else {
          setScorecard(
            buildBlankPointsScorecard(loadedFactors)
          );
        }
        
        logger.log("✅ Points scoring configuration loaded", {
          factorCount: loadedFactors.length,
        });

      } catch (err) {

        logger.error("❌ Failed loading scoring configuration", {
          value: err,
        });

        showStatus(setStatus, {
          type: "error",
          message: "Unable to load scoring configuration.",
        });

      } finally {

        setLoadingScorecard(false);

      }
    };

    loadScoringConfiguration();

  }, [eventId, votingType, eventDoc, loadedPointsVote]);

  // 🔍 Check if user already has a vote/scorecard for this entry or category
  // ✅ Standard Voting: looks for one previous vote in the same category
  // ✅ Ranked Choice: looks for one ranking document per voter/category
  // ✅ Points Voting: looks for one scorecard document per voter/entry
  useEffect(() => {
    if (!car || !eventId || !user || !votingType) return;

    const checkPreviousVote = async () => {
      try {
        const votesRef = collection(db, "events", eventId, "votes");

        // ✅ Points Voting uses document ID: {uid}_{cid}
        // ✅ This allows one editable scorecard per judge per entry.
        if (votingType === "points") {
          const cid = car.id || car.cid;
          const pointsVoteId = `${user.uid}_${cid}`;
          const pointsVoteRef = doc(db, "events", eventId, "votes", pointsVoteId);
          const pointsVoteSnap = await getDoc(pointsVoteRef);

          if (pointsVoteSnap.exists()) {
            const data = pointsVoteSnap.data();

            const loadedVote = {
              ...data,
              id: pointsVoteSnap.id,
            };

            setPrevVote(loadedVote);
            setLoadedPointsVote(loadedVote);

            logger.log("🔁 Previous points scorecard found:", {
              value: data,
            });
          } else {
            setPrevVote(null);
            setLoadedPointsVote(null);
          }

          return;
        }

        // ✅ Ranked Choice uses document ID: {uid}_{catid}
        if (votingType === "ranked_choice") {
          const catid = parseInt(car.catid);
          const rankedVoteId = `${user.uid}_${catid}`;
          const rankedVoteRef = doc(db, "events", eventId, "votes", rankedVoteId);
          const rankedVoteSnap = await getDoc(rankedVoteRef);

          if (rankedVoteSnap.exists()) {
            const data = rankedVoteSnap.data();

            setPrevVote({
              ...data,
              id: rankedVoteSnap.id,
            });

            logger.log("🔁 Previous ranked choice vote found:", {
              value: data,
            });
          } else {
            setPrevVote(null);
          }

          return;
        }

        // ✅ Standard Voting still checks by category
        if (car.catid === undefined) return;

        const q = query(
          votesRef,
          where("uid", "==", user.uid),
          where("catid", "==", parseInt(car.catid))
        );

        const snap = await getDocs(q);

        if (!snap.empty) {
          const data = snap.docs[0].data();

          setPrevVote({
            ...data,
            id: snap.docs[0].id,
          });

          logger.log("🔁 Previous standard vote found:", {
            value: data,
          });
        } else {
          setPrevVote(null);
        }
      } catch (err) {
        logger.error("❌ Error checking previous vote:", {
          value: err,
        });
      }
    };

    checkPreviousVote();
  }, [car, eventId, user, votingType]);
  
  // =====================================================
  // 🛡️ LGV Integrity Engine Vote Submitter
  // ✅ Purpose:
  // - Sends vote requests to trusted Cloud Function submitVote
  // - Keeps vote writes out of React
  // - Allows server to write both vote record and immutable audit record
  // =====================================================
  const submitVoteThroughIntegrityEngine = async (payload) => {
    const submitVote = httpsCallable(functions, "submitVote");

    const response = await submitVote({
      eventId,
      votingType,
      voteSource: getSessionValue("voteEntrySource") || "qr",
      ...payload,
    });

    return response.data;
  };

  // ✅ Submit or update a ranked choice vote
  // ✅ Ranked choice uses ONE vote document per voter per category
  // ✅ Document ID format: {uid}_{catid}
  // ✅ This allows voters to scan multiple entries and build their ranking over time
  const doRankedChoiceVote = async () => {

    if (!isVotingOpen || !car || car.catid === undefined || car.catid === 0 || !car.regcnt) {

      showStatus(setStatus, {
        type: "error",
        message: "Voting is not allowed right now.",
      });

      return;
    }

    try {
      const uid = auth.currentUser.uid;
      const catid = parseInt(car.catid);
      const cid = car.id || car.cid;
      const rankedVoteId = `${uid}_${catid}`;
      const rankedVoteRef = doc(db, "events", eventId, "votes", rankedVoteId);

      const ownerDisplayName =
        `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
        car.ownerName ||
        "Unknown Owner";

      const rankedVoteSnap = await getDoc(rankedVoteRef);
      const existingVote = rankedVoteSnap.exists() ? rankedVoteSnap.data() : {};
      const existingRankings = Array.isArray(existingVote.rankings)
        ? existingVote.rankings
        : [];

      // ✅ Ranked Choice Insert / Reorder Logic
      // ✅ Removes the current entry first.
      // ✅ Inserts it at the selected position.
      // ✅ Re-numbers all rankings sequentially.
      // ✅ Prevents gaps and duplicate rankings.
      //
      // Example:
      // Existing: 1=A, 2=B, 3=C
      // Re-save B as rank 2
      // Result: 1=A, 2=B, 3=C
      //
      // Example:
      // Existing: 1=A, 2=B, 3=C
      // Insert D at rank 2
      // Result: 1=A, 2=D, 3=B, 4=C

      const rankingsWithoutCurrentEntry = existingRankings
        .filter((ranking) => ranking.cid !== cid)
        .sort((a, b) => Number(a.rank) - Number(b.rank));

      const insertedRanking = {
        cid,
        rank: Number(selectedRank),
        regcnt: car.regcnt,
        description: car.description || "",
        name: ownerDisplayName,
      };

      // ✅ Convert rank to array position
      const insertIndex = Math.max(
        0,
        Number(selectedRank) - 1
      );

      // ✅ Insert entry into ordered rankings
      const reorderedRankings = [
        ...rankingsWithoutCurrentEntry.slice(0, insertIndex),
        insertedRanking,
        ...rankingsWithoutCurrentEntry.slice(insertIndex),
      ];

      // ✅ Re-number rankings sequentially
      const updatedRankings = reorderedRankings
        .map((ranking, index) => ({
          ...ranking,
          rank: index + 1,
        }))
        .filter((ranking) => ranking.rank <= maxRankChoices);
        
      const rankedVote = {
        uid,
        catid,
        eventId,
        votingType: "ranked_choice",
        rankings: updatedRankings,
        updatedAt: serverTimestamp(),
        createdAt: existingVote.createdAt || serverTimestamp(),
      };

      const integrityResult =
        await submitVoteThroughIntegrityEngine({
          entryId: cid,
          catid,
          rankings: updatedRankings,
        });

      logger.log("🛡️ Integrity Engine ranked vote result", {
        value: integrityResult,
      });
      
      // ✅ Immediately update local UI state so rankings refresh
      // ✅ without requiring a page reload.
      setPrevVote({
        ...rankedVote,
        id: rankedVoteId,
        rankings: updatedRankings,
      });

      clearPendingVoteNavigation();

      showStatus(setStatus, {
        type: "success",
        message: `This entry has been saved as your #${selectedRank} ranked choice.`,
      });

      logger.log("✅ Ranked choice vote saved", {
        rankedVoteId,
        selectedRank,
        rankings: updatedRankings,
      });

    } catch (err) {
      logger.error("❌ Error submitting ranked choice vote:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Ranked choice vote failed. Please try again.",
      });
    }
  };

  // ✅ Submit or update a Points / Scorecard vote
  // ✅ Points voting uses ONE vote document per voter per entry
  // ✅ Document ID format: {uid}_{cid}
  // ✅ This prevents duplicate scorecards and allows judges to revisit/edit an entry
  const doPointsVote = async () => {

    if (!isVotingOpen || !car || !car.regcnt) {

      showStatus(setStatus, {
        type: "error",
        message: "Voting is not allowed right now.",
      });

      return;
    }

    try {

      const uid = auth.currentUser.uid;
      const cid = car.id || car.cid;
      const catid = 1;
      const pointsVoteId = `${uid}_${cid}`;
      const pointsVoteRef = doc(db, "events", eventId, "votes", pointsVoteId);

      const preparedScores = preparePointsScoresForSave(scorecard);
      const totalPoints = calculatePointsBallotTotal(preparedScores);

      const validation = validatePointsBallot(
        preparedScores,
        scoringFactors,
        maxBallotPoints
      );

      if (!validation.isValid) {

        showStatus(setStatus, {
          type: "warning",
          message: validation.message,
        });

        return;
      }

      const ownerDisplayName =
        `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
        car.ownerName ||
        "Unknown Owner";

      const existingVoteSnap = await getDoc(pointsVoteRef);
      const existingVote = existingVoteSnap.exists()
        ? existingVoteSnap.data()
        : {};

      const pointsVote = {
        uid,
        cid,
        catid,
        eventId,
        votingType: "points",

        totalPoints,
        maxBallotPoints,
        scores: preparedScores,

        regcnt: car.regcnt,
        description: car.description || "",
        name: ownerDisplayName,
        ownerId: car.ownerId || "",

        updatedAt: serverTimestamp(),
        createdAt: existingVote.createdAt || serverTimestamp(),
      };

      const integrityResult =
        await submitVoteThroughIntegrityEngine({
          entryId: cid,
          totalPoints,
          maxBallotPoints,
          scores: preparedScores,
        });

      logger.log("🛡️ Integrity Engine points vote result", {
        value: integrityResult,
      });
      
      const savedLocalVote = {
        ...pointsVote,
        id: pointsVoteId,
      };

      setPrevVote(savedLocalVote);
      setLoadedPointsVote(savedLocalVote);

      await setDoc(doc(db, "users", uid), {
        votedEvents: arrayUnion(eventId),
      }, { merge: true });

      clearPendingVoteNavigation();

      showStatus(setStatus, {
        type: "success",
        message: `Scorecard saved. Final score: ${totalPoints} / ${maxBallotPoints}.`,
      });

      logger.log("✅ Points vote saved", {
        pointsVoteId,
        cid,
        totalPoints,
        maxBallotPoints,
        scores: preparedScores,
      });

    } catch (err) {

      logger.error("❌ Error submitting points vote:", {
        value: err,
      });

      showStatus(setStatus, {
        type: "error",
        message: "Scorecard save failed. Please try again.",
      });

    }
  };

  const doVote = async () => {

    trackEvent({
      action: GA_EVENTS.VOTE_SUBMIT_CLICKED,
      parameters: {
        event_slug: eventslug,
        event_id: eventId,
        entry_id: canonicalEntryId,
        voting_type: votingType,
        source: getSessionValue("voteEntrySource") || "qr",
        source_page: window.location.pathname,
      },
    });

    // ✅ Ranked Choice uses its own save flow
    if (votingType === "ranked_choice") {
      await doRankedChoiceVote();
      return;
    }

    // ✅ Points Voting uses its own scorecard save flow
    if (votingType === "points") {
      await doPointsVote();
      return;
    }

    // ✅ Future voting types are not implemented yet
    if (votingType !== "standard") {

      showStatus(setStatus, {
        type: "warning",
        message: `This event uses "${votingType}" voting. This voting screen has not been implemented yet.`,
      });

      return;
    }

    if (!isVotingOpen || !car || car.catid === undefined || car.catid === 0 || !car.regcnt) {

      showStatus(setStatus, {
        type: "error",
        message: "Voting is not allowed right now.",
      });

      return;
    }

    try {

      const ownerDisplayName =
        `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
        car.ownerName ||
        "Unknown Owner"; 

      const vote = {
        ...buildVoteDocumentForEvent({
          eventDoc,
          uid: auth.currentUser.uid,
          cid: car.id || car.cid,
          catid: parseInt(car.catid),
          eventId,
          bestInShow: voteBestInShow,
        }),

        // Legacy fields still used by ballot/audit/leaderboard
        regcnt: car.regcnt,
        description: car.description,
        name: ownerDisplayName,
        ownerId: car.ownerId || "",
      };

       const integrityResult =
        await submitVoteThroughIntegrityEngine({
          entryId: car.id || car.cid,
          bestInShow: voteBestInShow,
        });

      logger.log("🛡️ Integrity Engine standard vote result", {
        value: integrityResult,
      });

      setSessionValue(
        SESSION_KEYS.VOTE_CONFIRMATION,
        JSON.stringify(
          integrityResult?.confirmation || {
            cid: car.id || car.cid,
            catid: car.catid,
            regcnt: car.regcnt,
            description: car.description,
            name: ownerDisplayName,
            createdAt: new Date().toISOString(),
          }
        )
      );

      clearPendingVoteNavigation();

      // ✅ Only show Close Tab button when vote.js was opened directly by QR flow.
      // ✅ Manual vote flow should set voteEntrySource = "manual" before navigating here.
      const voteEntrySource = getSessionValue("voteEntrySource");

      if (voteEntrySource === "manual") {
        removeSessionValue("qrVoteTab");
        removeSessionValue("voteEntrySource");

        logger.log("🧾 Manual vote submitted — Close Tab button hidden.");
      } else {
        setSessionValue("qrVoteTab", "true");

        logger.log("📱 QR vote submitted — Close Tab button enabled.");
      }

      trackEvent({
        action:
          voteEntrySource === "manual"
            ? GA_EVENTS.MANUAL_VOTE_SUBMITTED
            : GA_EVENTS.VOTE_SUBMITTED,
        parameters: {
          event_slug: eventslug,
          event_id: eventId,
          entry_id: car.id || car.cid,
          category_id: car.catid,
          voting_type: votingType,
          source: voteEntrySource || "qr",
          best_in_show: voteBestInShow === true,
          source_page: window.location.pathname,
        },
      });
            
      navigate(`/${eventslug}/ballot?vote`);

    } catch (err) {
      logger.error("❌ Error submitting vote:", {
        value: err
      });

      showStatus(setStatus, {
        type: "error",
        message: "Vote failed. Please try again.",
      });

    }
  };

  const voteSeo = {
    title: `Vote | ${eventDoc?.name || "Event"} | Let's Go Vote`,
    description: "Vote confirmation page.",
    path: `/${eventslug}/vote/${car?.entrySlug || id}`,
    noindex: true,
  };

  if (!loading && user && !user.emailVerified) {
    navigate("/dashboard?verifyRequired=1", { replace: true });
    return null;
  }

  if (
    authLoading ||
    roleLoading ||
    eventLoading ||
    votingLoading ||
    carLoading ||
    loadingScorecard ||
    !ready ||
    !car
  ) {
    return <FullPageSpinner message="Loading Voting Event..." />;
  }

  logger.log("Pending Vote Session", {
    pendingVoteCid: getSessionValue(
      SESSION_KEYS.PENDING_VOTE_CID
    ),

    entryLabel: getSessionValue(
      SESSION_KEYS.PENDING_VOTE_ENTRY_LABEL
    ),

    categoryLabel: getSessionValue(
      SESSION_KEYS.PENDING_VOTE_CATEGORY_LABEL
    ),
  });
  
  return (
  <>
    <SeoHead {...voteSeo} />
    <section id="top-content" className="container-fluid py-5">
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">✅ Confirm Your Vote</h1>
          <p className="lead text-muted">You're about to cast your vote for this entry</p>
          <div className="alert alert-secondary">

            Voting Type:{" "}
            <strong>{votingTypeLabel}</strong>

          </div>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
          >
            {status.message}
          </div>
        )}

        <div className="list-group mt-3">
          <div className="list-group-item">
            <strong>{categoryName}</strong>
            <br />
            ✅ #{car.regcnt} — {car.description}
            <br />
            Owner: {
            `${car.ownerFirst || ""} ${car.ownerLast || ""}`.trim() ||
            car.ownerName ||
            "Unknown Owner"
            }
          </div>

          {/* ✅ Standard Voting previous vote warning */}
          {/* ✅ Only show this for standard voting because standard voting replaces one vote with another */}
          {prevVote && votingType === "standard" && (

            <div className="list-group-item text-danger">
              ⚠️ This will replace your previous vote:
              <br />
              #{prevVote.regcnt} — {prevVote.description} by {prevVote.name}
            </div>
          )}

          {votingType === "ranked_choice" && (
            <div className="alert alert-primary mt-3">

              <h5>🗳 Ranked Choice Voting</h5>

              <p className="mb-3">
                Choose how you want to rank this entry in <strong>{categoryName}</strong>.
              </p>

              {votingType === "ranked_choice" && prevVote?.rankings?.length > 0 && (
                <div className="mb-3">
                  <strong>Your Current Rankings:</strong>

                  <ol className="mb-0 mt-2">
                    {[...prevVote.rankings]
                      .sort((a, b) => Number(a.rank) - Number(b.rank))
                      .map((ranking) => (
                        <li key={`${ranking.rank}-${ranking.cid}`}>
                          #{ranking.regcnt} — {ranking.description} by {ranking.name}
                        </li>
                      ))}
                  </ol>
                </div>
              )}

              <select
                className="form-select"
                value={selectedRank}
                onChange={(e) => setSelectedRank(Number(e.target.value))}
              >
                <option value={1}>1st Choice</option>
                <option value={2}>2nd Choice</option>
                <option value={3}>3rd Choice</option>
                <option value={4}>4th Choice</option>
                <option value={5}>5th Choice</option>
              </select>

              <div className="small mt-2 text-muted">
                Selecting a rank will insert this entry at that position. Existing choices at that rank and below will move down.
              </div>

            </div>
          )}

          {votingType === "points" && (
            <div className="mt-3">

              <div className="alert alert-primary">

                <h5>
                  {isScorecardMode
                    ? "📋 Detailed Scorecard"
                    : "🏆 Points Judging"}
                </h5>

                <p className="mb-0">
                  {isScorecardMode
                    ? "Score each judging criterion below."
                    : "Score this entry using the judging criteria below."}
                </p>

              </div>

              {prevVote && votingType === "points" && (
                <div className="alert alert-success">
                  <strong>Previously Scored</strong>
                  <br />
                  You have already scored this entry. Saving again will update your scorecard.
                </div>
              )}

              {isScorecardMode && (
                <div className="alert alert-info">
                  <strong>Detailed Scorecard Mode</strong>
                  <br />
                  Scores entered for each highlight will automatically roll up into the factor totals.
                </div>
              )}

              <PointsScorecardForm
                scorecard={scorecard}
                setScorecard={setScorecard}
                maxBallotPoints={maxBallotPoints}
              />

            </div>
          )}

        </div>

        <div className="d-flex gap-3 mt-4 flex-wrap">

          {showBestInShowOption && (
            <div className="alert alert-info">
              You may optionally select one entry as your Best in Show vote.
            </div>
          )}

          {showBestInShowOption && (
            <button
              className={`btn ${voteBestInShow ? "btn-warning" : "btn-outline-secondary"}`}
              onClick={() => setVoteBestInShow(!voteBestInShow)}
            >
              {voteBestInShow ? "🏆 Best in Show Selected" : "🏆 Also Vote Best in Show"}
            </button>
          )}

          <button onClick={doVote} className="btn btn-success" disabled={!isVotingOpen}>

            {votingType === "ranked_choice"
              ? "✅ Save Ranking"
              : votingType === "points"
              ? "✅ Save Scorecard"
              : "✅ Submit Vote"}

          </button>

          <button
            onClick={() => {
              trackEvent({
                action: GA_EVENTS.VOTE_CANCELLED,
                parameters: {
                  event_slug: eventslug,
                  event_id: eventId,
                  entry_id: canonicalEntryId,
                  voting_type: votingType,
                  source_page: window.location.pathname,
                },
              });

              navigate(`/${eventslug}/ballot?cancel=1`);
            }}
            className="btn btn-outline-secondary"
            >
            ❌ Cancel
          </button>

          <button
            type="button"
            className="btn btn-outline-primary ms-2"
            onClick={() => navigate(`/${eventslug}/manual_vote`)}
          >
            Browse Entries
          </button>

        </div>
      </div>
    </section>
  </>
  );
};

export default Vote;
